import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PauseIcon, PlayIcon } from '@/components/icons';
import { getAudioContextCtor } from '@/lib/voice/audioContext';

/**
 * Telegram-style voice-note player: a round play/pause button, a clickable
 * waveform (peaks decoded from the clip), an mm:ss timer and a playback-speed
 * pill.
 *
 * The clip is fetched over CORS and played from a local `audio/webm` Blob URL
 * — this sidesteps cross-origin / Content-Type / range-request quirks that
 * stopped some browsers from playing the `:9000` resource directly. Decoding
 * the same bytes via the Web Audio API yields both the waveform peaks AND the
 * exact duration (MediaRecorder WebM lacks a duration header → `Infinity`).
 */
const BAR_COUNT = 28;
const FLAT_PEAKS = Array.from({ length: BAR_COUNT }, () => 0.35);

export function VoiceNotePlayer(props: { src: string; durationMs?: number }) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState<number | null>(
    props.durationMs && props.durationMs > 0 ? props.durationMs / 1000 : null,
  );
  const [peaks, setPeaks] = useState<number[] | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [speed, setSpeed] = useState(1);

  // Fetch the clip → Blob URL for playback, and decode it for waveform peaks
  // + exact duration. Decoding never blocks playback (best-effort).
  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    fetch(props.src)
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`http_${r.status}`))))
      .then(async (buf) => {
        if (cancelled) return;
        created = URL.createObjectURL(new Blob([buf], { type: 'audio/webm' }));
        setObjectUrl(created);
        let actx: AudioContext | null = null;
        try {
          const Ctor = getAudioContextCtor();
          actx = new Ctor();
          const audioBuf = await actx.decodeAudioData(buf.slice(0));
          if (!cancelled) {
            if (audioBuf.duration > 0) setDuration(audioBuf.duration);
            setPeaks(computePeaks(audioBuf, BAR_COUNT));
          }
        } catch {
          /* keep flat peaks; duration falls back to durationMs / metadata */
        } finally {
          // Always release the context — leaking one per failed decode
          // exhausts the browser's AudioContext budget over a long session.
          void actx?.close();
        }
      })
      .catch(() => {
        /* fall back to the direct src for playback */
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [props.src]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setPosition(el.currentTime);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      setPosition(0);
    };
    const onMeta = () => {
      if (Number.isFinite(el.duration) && el.duration > 0) setDuration(el.duration);
    };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('durationchange', onMeta);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('durationchange', onMeta);
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed, objectUrl]);

  function toggle() {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) void el.play().catch(() => setPlaying(false));
    else el.pause();
  }

  function seekToRatio(ratio: number) {
    const el = audioRef.current;
    if (!el || !duration) return;
    const t = Math.max(0, Math.min(1, ratio)) * duration;
    el.currentTime = t;
    setPosition(t);
  }

  const bars = peaks ?? FLAT_PEAKS;
  const progress = duration && duration > 0 ? Math.min(1, position / duration) : 0;
  const display = formatDuration(Math.round(playing || position > 0 ? position : (duration ?? 0)));
  const audioSrc = objectUrl ?? props.src;

  return (
    <div className="flex w-fit max-w-[300px] items-center gap-1.5 rounded-full border border-separator bg-surface-secondary py-1 pl-1 pr-2">
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? t('cmod.voiceNote.pause') : t('cmod.voiceNote.play')}
        className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-accent text-white transition-transform hover:scale-105 active:scale-95"
      >
        {playing ? <PauseIcon className="size-3.5" /> : <PlayIcon className="size-3.5 translate-x-px" />}
      </button>

      <div
        className="flex h-5 min-w-0 flex-1 cursor-pointer items-center gap-[2px] overflow-hidden"
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          seekToRatio((e.clientX - rect.left) / rect.width);
        }}
      >
        {bars.map((p, i) => {
          const played = i / bars.length <= progress;
          // Fixed px width + px height — no percentage-height resolution
          // needed, so the bars are always visible regardless of layout.
          return (
            <span
              key={i}
              className={
                'block w-[2px] shrink-0 rounded-full transition-colors ' +
                (played ? 'bg-accent' : 'bg-accent/30')
              }
              style={{ height: `${Math.max(3, Math.round(p * 16))}px` }}
            />
          );
        })}
      </div>

      <span className="shrink-0 font-mono text-[10px] tabular-nums text-muted">{display}</span>

      <button
        type="button"
        onClick={() => setSpeed((s) => (s === 1 ? 1.5 : s === 1.5 ? 2 : 1))}
        aria-label={t('cmod.voiceNote.speed')}
        className="font-semibold shrink-0 rounded-full px-1 text-[10px] tabular-nums text-muted transition-colors hover:text-foreground"
      >
        {speed}×
      </button>

      <audio ref={audioRef} src={audioSrc} preload="metadata" className="hidden" />
    </div>
  );
}

/** Downsample an AudioBuffer to `bars` normalised peak amplitudes (0..1). */
function computePeaks(audioBuffer: AudioBuffer, bars: number): number[] {
  const data = audioBuffer.getChannelData(0);
  const block = Math.floor(data.length / bars) || 1;
  const peaks: number[] = [];
  for (let i = 0; i < bars; i += 1) {
    let max = 0;
    const start = i * block;
    for (let j = 0; j < block; j += 1) {
      const v = Math.abs(data[start + j] ?? 0);
      if (v > max) max = v;
    }
    peaks.push(max);
  }
  const norm = Math.max(...peaks, 0.01);
  // Floor so silent gaps still render a faint bar (Telegram look).
  return peaks.map((p) => Math.max(0.12, p / norm));
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
